import { useMutation, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { organizationsApi } from '../../services/api'
import type { AllPermissions, ObjectPermissionsResponse } from '../../app/modules/accounts/components/organization/types'
import type { RoleComponentGrant } from '../../app/modules/types/componentAccess'

/** The role-permission scope behind "All components". */
export const ALL_COMPONENTS_SCOPE = 'api:component'

/**
 * Components that can be given to a role. The backend lists only those inside
 * the organization's plan, so everything returned can be granted. A role that
 * holds "All components" shows `has_access: false` on every row — that listing
 * covers per-component grants only.
 */
export function useRoleComponents(organizationId: string | null, roleId: string | null) {
  return useQuery({
    queryKey: queryKeys.permissions.roleComponents(organizationId || '', roleId || ''),
    queryFn: async (): Promise<ObjectPermissionsResponse> =>
      organizationsApi.getObjectPermissions(organizationId!, roleId!, 'component'),
    enabled: !!organizationId && !!roleId,
    // A role's grants change under the admin's hands; never serve a stale list.
    staleTime: 0,
  })
}

/** Does the role hold the ordinary `api:component` / `use` permission? */
export function roleHoldsAllComponents(permissions: AllPermissions | undefined): boolean {
  if (!permissions) return false
  for (const category of Object.values(permissions)) {
    for (const resource of Object.values(category)) {
      if (resource?.api?.scope === ALL_COMPONENTS_SCOPE) {
        return resource.api.actions?.use?.is_granted === true
      }
    }
  }
  return false
}

/**
 * Save a role's COMPLETE component set in one call (not a diff). An empty list
 * removes every per-component grant but NOT the all-components permission.
 */
export function useSaveRoleComponents() {
  return useMutation({
    mutationFn: ({
      organizationId,
      roleId,
      grants,
    }: {
      organizationId: string
      roleId: string
      grants: RoleComponentGrant[]
    }) => organizationsApi.saveRoleComponents(organizationId, roleId, grants),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.roleComponents(variables.organizationId, variables.roleId),
      })
      // The admin's own access may have just changed.
      queryClient.invalidateQueries({ queryKey: queryKeys.permissions.componentAccess(variables.organizationId) })
    },
  })
}
